import { memo } from 'react';
import { parseMarkup, hasMarkup, markupRuns } from './transcriptMarkup';
import './MarkedTranscript.css';

const CIRCLED = '①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳';
const commentNumber = (n) => CIRCLED[n - 1] || `(${n})`;

/**
 * A teacher's corrected transcript with her marks, the way the child reads it:
 * highlighted words, struck words (with her fix right after, like a suggested
 * edit), and comments numbered on their words and listed underneath.
 *
 * `markup` is the parsed object or the stored JSON text from a submission row.
 * `renderWord` lets the teacher's annotator make each word tappable.
 */
export const MarkedTranscript = memo(function MarkedTranscript({ markup: raw, renderWord = null, className = '' }) {
  const markup = parseMarkup(raw);
  if (!hasMarkup(markup)) return null;

  const comments = [];
  const runs = markupRuns(markup).map((run) => {
    const words = run.words.map((word, k) => {
      const index = run.start + k;
      const sep = k > 0 ? ' ' : '';
      return <span key={index}>{sep}{renderWord ? renderWord(word, index) : word}</span>;
    });
    const { mark } = run;
    if (!mark) return <span key={run.start}>{words}</span>;

    // A strike's comment is the word that goes there, so it reads in place.
    const fixInPlace = mark.style === 'strike' && mark.comment;
    let number = null;
    if (mark.comment && !fixInPlace) {
      comments.push({ number: comments.length + 1, comment: mark.comment, words: run.words.join(' ') });
      number = comments.length;
    }
    const cls = [
      mark.style === 'highlight' && 'mt-highlight',
      mark.style === 'strike' && 'mt-strike',
      number && 'mt-commented',
    ].filter(Boolean).join(' ');
    return (
      <span key={run.start}>
        <span className={cls}>{words}</span>
        {number && <sup className="mt-number">{commentNumber(number)}</sup>}
        {fixInPlace && <> <span className="mt-fix">{mark.comment}</span></>}
      </span>
    );
  });

  return (
    <div className={`marked-transcript ${className}`}>
      <p className="mt-text">
        {runs.map((run, i) => <span key={i}>{i > 0 ? ' ' : ''}{run}</span>)}
      </p>
      {comments.length > 0 && (
        <ol className="mt-comments">
          {comments.map((c) => (
            <li key={c.number} className="mt-comment-item">
              <span className="mt-number">{commentNumber(c.number)}</span>
              <span className="mt-comment-words">「{c.words}」</span>
              <span>{c.comment}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
});

export default MarkedTranscript;
